import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import orderBy from "lodash/orderBy";

import Grid from "ui/Grid";
import { type GridColumn, type SortOrder } from "ui/Grid/util";

type SelectCellState = {
  selected: Set<string>;
  locked: Set<string>;
  labelById: Map<string, string>;
  disabled: boolean;
  disabledTitle: string;
  onShiftDown: (shiftKey: boolean) => void;
  onToggle: (id: string) => void;
};

const SelectCellContext = createContext<SelectCellState | null>(null);

function useSelectCellContext(): SelectCellState {
  const ctx = useContext(SelectCellContext);
  if (!ctx) {
    throw new Error(
      "useSelectCellContext must be used within a SelectableGrid",
    );
  }
  return ctx;
}

const SelectCell = ({ rowData }: { rowData: { id: string } }) => {
  const state = useSelectCellContext();
  const { id } = rowData;
  const locked = state.locked.has(id);
  const label = state.labelById.get(id);
  return (
    <input
      type="checkbox"
      checked={locked || state.selected.has(id)}
      disabled={locked || state.disabled}
      title={locked ? state.disabledTitle : undefined}
      onMouseDown={(e) => state.onShiftDown(e.shiftKey)}
      onChange={() => state.onToggle(id)}
      aria-label={label != null ? `Select ${label}` : "Select"}
      className={locked ? "cursor-not-allowed" : "cursor-pointer"}
    />
  );
};

type Props<T> = {
  items: T[];
  columns: GridColumn<T, keyof T>[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabledIds?: string[];
  disabledTitle?: string;
  disabled?: boolean;
  searchValue: (item: T) => string;
  sortValue: (item: T, field: keyof T) => string | number;
  defaultSortBy: keyof T;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectAllLabel?: string;
  itemLabel?: (item: T) => string;
};

// Generic selectable grid includes search, column sorting, and checkbox selection with
// shift-click range selection and a select-all header checkbox over the filtered rows.
// Built on the shared Grid. Caller supplies data columns.
function SelectableGrid<T extends { id: string }>({
  items,
  columns,
  selectedIds,
  onChange,
  disabledIds,
  disabledTitle = "Already selected elsewhere",
  disabled,
  searchValue,
  sortValue,
  defaultSortBy,
  searchPlaceholder = "Search...",
  emptyMessage = "No matches.",
  selectAllLabel = "Select all",
  itemLabel,
}: Props<T>) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{
    sortBy: keyof T;
    sortDirection: SortOrder;
  }>({ sortBy: defaultSortBy, sortDirection: "ASC" });

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const disabledSet = useMemo(() => new Set(disabledIds ?? []), [disabledIds]);
  const lastIdRef = useRef<string | null>(null);
  const shiftRef = useRef(false);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) =>
      searchValue(item).toLowerCase().includes(query),
    );
  }, [items, search, searchValue]);

  const displayed = useMemo(() => {
    const order = sort.sortDirection === "ASC" ? "asc" : "desc";
    return orderBy(
      filtered,
      [(item: T) => sortValue(item, sort.sortBy)],
      [order],
    );
  }, [filtered, sort.sortBy, sort.sortDirection, sortValue]);

  const indexById = useMemo(() => {
    const byId = new Map<string, number>();
    displayed.forEach((item, i) => byId.set(item.id, i));
    return byId;
  }, [displayed]);

  const toggle = (id: string) => {
    if (disabledSet.has(id)) {
      return;
    }
    const index = indexById.get(id) ?? -1;
    const willSelect = !selectedSet.has(id);
    const next = new Set(selectedSet);
    const lastIndex =
      lastIdRef.current != null ? indexById.get(lastIdRef.current) : undefined;
    if (shiftRef.current && lastIndex != null && index >= 0) {
      const [from, to] = [lastIndex, index].sort((a, b) => a - b);
      for (let i = from; i <= to; i++) {
        const rowId = displayed[i].id;
        if (disabledSet.has(rowId)) {
          continue;
        }
        if (willSelect) {
          next.add(rowId);
        } else {
          next.delete(rowId);
        }
      }
    } else if (willSelect) {
      next.add(id);
    } else {
      next.delete(id);
    }
    lastIdRef.current = id;
    shiftRef.current = false;
    onChange(Array.from(next));
  };

  // Select all and indeterminate only consider rows the user can actually toggle
  const selectable = displayed.filter((item) => !disabledSet.has(item.id));
  const allSelected =
    selectable.length > 0 &&
    selectable.every((item) => selectedSet.has(item.id));
  const someSelected = selectable.some((item) => selectedSet.has(item.id));
  const toggleAll = () => {
    const next = new Set(selectedSet);
    selectable.forEach((item) => {
      if (allSelected) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
    });
    onChange(Array.from(next));
  };
  const headerRef = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) {
        el.indeterminate = !allSelected && someSelected;
      }
    },
    [allSelected, someSelected],
  );

  const handleSort = (field: keyof T, order: SortOrder) => {
    setSort({ sortBy: field, sortDirection: order });
  };

  const allColumns: GridColumn<T, keyof T>[] = [
    {
      field: "id" as keyof T & string,
      header: (
        <input
          type="checkbox"
          ref={headerRef}
          checked={allSelected}
          disabled={disabled}
          onChange={toggleAll}
          aria-label={selectAllLabel}
          className="cursor-pointer"
        />
      ),
      width: "36px",
      disableSort: true,
      disableResize: true,
      renderer: SelectCell,
    },
    ...columns,
  ];

  const cellState: SelectCellState = {
    selected: selectedSet,
    locked: disabledSet,
    labelById: new Map(
      itemLabel ? items.map((item) => [item.id, itemLabel(item)]) : [],
    ),
    disabled: !!disabled,
    disabledTitle,
    onShiftDown: (shiftKey) => {
      shiftRef.current = shiftKey;
    },
    onToggle: toggle,
  };

  return (
    <SelectCellContext.Provider value={cellState}>
      <div>
        <div className="flex items-center gap-[8px] mb-[6px]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="form-control flex-1 min-w-0"
          />
          <span className="text-[12px] text-[#606060] flex-none min-w-[80px]">
            {selectedSet.size} selected
          </span>
        </div>
        {displayed.length === 0 ? (
          <p className="text-[#606060]">{emptyMessage}</p>
        ) : (
          <Grid
            striped
            className="border-b border-[#e8e8ee]"
            data={displayed}
            columns={allColumns}
            pageSize={10}
            footer={
              <span className="text-[#606060] text-[12px]">
                Tip: hold Shift to select a range.
              </span>
            }
            sortedBy={sort.sortBy}
            sortedOrder={sort.sortDirection}
            handleSort={handleSort}
          />
        )}
      </div>
    </SelectCellContext.Provider>
  );
}

export default SelectableGrid;
